import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { Error } from "../../../common/components/AppState/Error";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { Input, InputIcon } from "../../../common/components/Input";
import { ButtonText } from "../../../common/components/Button";
import { FloatingTooltip } from "../../../common/components/Tooltip/FloatingTooltip";
import { profileRowClassName } from "../../../common/components/Input/ProfileInputStyle";
import { setAlert } from "../../alert/state/alertsSlice";
import {
  clearEnrollmentWizardState,
  disableMfa,
  fetchMfaStatus,
  getMfaStatus,
  regenerateRecoveryCodes,
  sendMfaStepUpCode,
} from "../state/mfaSlice";
import MfaEnrollDialog from "./MfaEnrollDialog";
import RecoveryCodesDialog from "./RecoveryCodesDialog";

const MFA_DISABLE_COMPONENT_ID = "mfaDisableComponent";
const MFA_REGENERATE_COMPONENT_ID = "mfaRegenerateComponent";
const MFA_STEP_UP_COMPONENT_ID = "mfaSectionStepUpComponent";
const CODE_PATTERN = /^\d{6}$/;
const RECOVERY_CODES_LOW_THRESHOLD = 3;
const STEP_UP_COOLDOWN_SECONDS = 30;

type CredentialConfirmAction = "disable" | "regenerate";

interface MfaSectionProps {
  componentId: string;
}

const MfaSection = ({ componentId }: MfaSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const status = useAppSelector(getMfaStatus);

  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [isReplacementFlow, setIsReplacementFlow] = useState(false);
  const [recoveryCodesDialogOpen, setRecoveryCodesDialogOpen] = useState(false);

  const [credentialConfirmAction, setCredentialConfirmAction] =
    useState<CredentialConfirmAction | null>(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");

  const [stepUpToken, setStepUpToken] = useState<string | null>(null);
  const [stepUpSent, setStepUpSent] = useState(false);
  const [stepUpSending, setStepUpSending] = useState(false);
  const [stepUpCooldown, setStepUpCooldown] = useState(0);

  useEffect(() => {
    void dispatch(fetchMfaStatus({ componentId }));
  }, []);

  useEffect(() => {
    if (stepUpCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setStepUpCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [stepUpCooldown]);

  const openActivate = () => {
    setIsReplacementFlow(false);
    setEnrollDialogOpen(true);
  };

  const openChangeMethod = () => {
    setIsReplacementFlow(true);
    setEnrollDialogOpen(true);
  };

  const closeEnrollDialog = () => {
    dispatch(clearEnrollmentWizardState());
    setEnrollDialogOpen(false);
  };

  const handleEnrolled = () => {
    setEnrollDialogOpen(false);
    setRecoveryCodesDialogOpen(true);
    void dispatch(fetchMfaStatus({ componentId }));
    dispatch(
      setAlert({
        type: "success",
        msg: t(
          isReplacementFlow
            ? "alerts.mfaMethodChangedSuccessfully"
            : "alerts.mfaEnabledSuccessfully",
        ),
      }),
    );
  };

  const closeRecoveryCodesDialog = () => {
    dispatch(clearEnrollmentWizardState());
    setRecoveryCodesDialogOpen(false);
  };

  const openCredentialConfirm = (action: CredentialConfirmAction) => {
    setConfirmPassword("");
    setConfirmCode("");
    setStepUpToken(null);
    setStepUpSent(false);
    setStepUpSending(false);
    setStepUpCooldown(0);
    setCredentialConfirmAction(action);
  };

  const closeCredentialConfirm = () => {
    setCredentialConfirmAction(null);
    setConfirmPassword("");
    setConfirmCode("");
    setStepUpToken(null);
    setStepUpSent(false);
    setStepUpSending(false);
    setStepUpCooldown(0);
  };

  const handleSendStepUp = () => {
    if (stepUpCooldown > 0 || stepUpSending) {
      return;
    }
    setStepUpSending(true);
    setStepUpSent(false);
    void dispatch(sendMfaStepUpCode({ componentId: MFA_STEP_UP_COMPONENT_ID }))
      .unwrap()
      .then((res) => {
        setStepUpToken(res.step_up_token);
        setStepUpSent(true);
        setStepUpSending(false);
        setStepUpCooldown(STEP_UP_COOLDOWN_SECONDS);
      })
      .catch(() => {
        setStepUpSending(false);
        setStepUpSent(false);
      });
  };

  const handleCredentialConfirmSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmedCode = confirmCode.trim();
    const isSixDigitCode = CODE_PATTERN.test(trimmedCode);
    const isEmailMethod = status?.method === "email";
    const canUseStepUp = isEmailMethod && Boolean(stepUpToken) && isSixDigitCode;
    const codeArgs =
      isEmailMethod && !canUseStepUp
        ? { recoveryCode: trimmedCode }
        : canUseStepUp
        ? { otp: trimmedCode, stepUpToken: stepUpToken as string }
        : isSixDigitCode
        ? { otp: trimmedCode }
        : { recoveryCode: trimmedCode };

    if (credentialConfirmAction === "disable") {
      void dispatch(
        disableMfa({
          componentId: MFA_DISABLE_COMPONENT_ID,
          password: confirmPassword,
          ...codeArgs,
        }),
      ).then((res) => {
        if (disableMfa.fulfilled.match(res)) {
          closeCredentialConfirm();
          dispatch(setAlert({ type: "success", msg: t("alerts.mfaDisabledSuccessfully") }));
        }
      });
    } else if (credentialConfirmAction === "regenerate") {
      void dispatch(
        regenerateRecoveryCodes({
          componentId: MFA_REGENERATE_COMPONENT_ID,
          password: confirmPassword,
          ...codeArgs,
        }),
      ).then((res) => {
        if (regenerateRecoveryCodes.fulfilled.match(res)) {
          closeCredentialConfirm();
          setRecoveryCodesDialogOpen(true);
          dispatch(
            setAlert({ type: "success", msg: t("alerts.mfaRecoveryCodesRegeneratedSuccessfully") }),
          );
        }
      });
    }
  };

  const statusLabel = !status
    ? ""
    : status.enabled && status.method === "totp"
    ? t("pages.profile.mfa.statusEnabledTotp")
    : status.enabled && status.method === "email"
    ? t("pages.profile.mfa.statusEnabledEmail")
    : t("pages.profile.mfa.statusDisabled");

  return (
    <>
      <h2 className="text-2xl font-medium mb-3 mt-8">{t("pages.profile.mfa.sectionTitle")}</h2>
      <p className="text-sm text-gray-500 mb-2">{t("pages.profile.mfa.sectionDescription")}</p>

      <div className="completely-centeredProfile">
        <IsLoading componentId={componentId} showSuccess={false} />
      </div>

      <div className={profileRowClassName}>
        <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
          {t("pages.profile.mfa.statusLabel")}
        </p>
        <p className="flex-1 text-sm font-medium text-center sm:text-left">{statusLabel}</p>
        <div className="flex shrink-0 items-center justify-end gap-2">
          {!status?.enabled && (
            <ButtonText
              type="button"
              text={t("pages.profile.mfa.activate")}
              onClick={openActivate}
            />
          )}
          {status?.enabled && (
            <>
              <div className="border-r-[1px] mr-2 pr-2 border-gray-300">
                <ButtonText
                  type="button"
                  text={t("pages.profile.mfa.changeMethod")}
                  onClick={openChangeMethod}
                />
              </div>
              <FloatingTooltip
                content={status.mandatory ? t("pages.profile.mfa.mandatoryHint") : ""}
                wrap
              >
                <button
                  type="button"
                  className="btn-danger-fill"
                  disabled={status.mandatory}
                  onClick={() => openCredentialConfirm("disable")}
                >
                  {t("pages.profile.mfa.deactivate")}
                </button>
              </FloatingTooltip>
            </>
          )}
        </div>
      </div>

      <p
        className={`w-full text-xs text-gray-500 px-2 xl:px-4 pt-1 ${
          status?.enabled && status.mandatory ? "" : "invisible"
        }`}
      >
        {t("pages.profile.mfa.mandatoryHint")}
      </p>

      {status?.enabled && (
        <div className={profileRowClassName}>
          <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
            {t("pages.profile.mfa.recoveryCodesRemainingLabel")}
          </p>
          <p className="flex-1 text-sm font-medium text-center sm:text-left">
            {status.recoveryCodesRemaining ?? "-"}
          </p>
          <div className="flex grow justify-end">
            <button
              type="button"
              className="btn-danger-fill"
              onClick={() => openCredentialConfirm("regenerate")}
            >
              {t("pages.profile.mfa.regenerateRecoveryCodes")}
            </button>
          </div>
        </div>
      )}

      <p
        className={`w-full text-xs text-amber-600 px-2 xl:px-4 pt-1 ${
          status?.enabled &&
          status.recoveryCodesRemaining !== null &&
          status.recoveryCodesRemaining !== undefined &&
          status.recoveryCodesRemaining <= RECOVERY_CODES_LOW_THRESHOLD
            ? ""
            : "invisible"
        }`}
      >
        {t("pages.profile.mfa.recoveryCodesLowWarning")}
      </p>

      <MfaEnrollDialog
        isOpen={enrollDialogOpen}
        isReplacement={isReplacementFlow}
        currentMethod={status?.method ?? null}
        onClose={closeEnrollDialog}
        onEnrolled={handleEnrolled}
      />

      <RecoveryCodesDialog isOpen={recoveryCodesDialogOpen} onClose={closeRecoveryCodesDialog} />

      <ModalStandard
        id="modalMfaCredentialConfirm"
        fullScreen={false}
        controlledMode={{ isOpen: credentialConfirmAction !== null }}
        showCloseButton
        onClose={closeCredentialConfirm}
      >
        <div className="flex w-full">
          <div className="completely-centered z-50">
            <IsLoading componentId={MFA_DISABLE_COMPONENT_ID} showSuccess={false} />
            <IsLoading componentId={MFA_REGENERATE_COMPONENT_ID} showSuccess={false} />
            <IsLoading componentId={MFA_STEP_UP_COMPONENT_ID} showSuccess={false} />
          </div>
          <div className="relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto">
            <h2 className="pt-3 pb-1 font-medium text-lg">
              {credentialConfirmAction === "disable"
                ? t("pages.profile.mfa.confirmDisableTitle")
                : t("pages.profile.mfa.confirmRegenerateTitle")}
            </h2>
            <h3 className="pb-3">{t("pages.profile.mfa.confirmSubtitle")}</h3>
            <div className="bg-red-100 rounded-lg p-3 text-red-700 font-medium text-sm mb-3">
              {credentialConfirmAction === "disable"
                ? t("pages.profile.mfa.confirmDisableWarning")
                : t("pages.profile.mfa.confirmRegenerateWarning")}
            </div>
            <form onSubmit={handleCredentialConfirmSubmit}>
              <div className="mb-2">
                <InputIcon
                  autoFocus
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(event.target.value)
                  }
                  type={confirmPasswordVisible ? "text" : "password"}
                  position="right"
                  placeholder={t("pages.profile.mfa.passwordPlaceholder")}
                  handleClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                  icon={confirmPasswordVisible ? "EyeIcon" : "EyeOffIcon"}
                />
              </div>
              <Input
                type="text"
                value={confirmCode}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmCode(event.target.value)
                }
                className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 border-0"
                placeholder={
                  status?.method === "email" && !stepUpToken
                    ? t("pages.profile.mfa.enroll.recoveryCodeOnlyPlaceholder")
                    : t("pages.profile.mfa.enroll.currentCodePlaceholder")
                }
              />
              {status?.method === "email" && (
                <div className="flex items-center mt-1">
                  <ButtonText
                    type="button"
                    text={
                      stepUpCooldown > 0
                        ? t("pages.profile.mfa.enroll.stepUpCooldown", { seconds: stepUpCooldown })
                        : t("pages.profile.mfa.enroll.emailStepUpButton")
                    }
                    onClick={handleSendStepUp}
                    disabled={stepUpCooldown > 0 || stepUpSending}
                  />
                </div>
              )}
              <p
                className={`text-xs text-green-600 mt-1 ${
                  stepUpSent && status?.method === "email" ? "" : "invisible"
                }`}
              >
                {t("pages.profile.mfa.enroll.emailStepUpSent")}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {status?.method === "email"
                  ? t("pages.profile.mfa.enroll.replaceHintEmail")
                  : t("pages.profile.mfa.enroll.replaceHint")}
              </p>

              <Error
                componentId={MFA_STEP_UP_COMPONENT_ID}
                errorString={t("pages.profile.mfa.enroll.stepUpSendFailed")}
              />
              <Error componentId={MFA_DISABLE_COMPONENT_ID} />
              <Error componentId={MFA_REGENERATE_COMPONENT_ID} />

              <div className="flex justify-end mt-3 space-x-3">
                <button
                  type="button"
                  className="btn-alternative-outline"
                  onClick={closeCredentialConfirm}
                >
                  {t("general.cancel")}
                </button>
                <button
                  className="btn-danger-fill"
                  type="submit"
                  disabled={!confirmPassword || !confirmCode.trim()}
                >
                  {t("general.confirm")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </ModalStandard>
    </>
  );
};

export default MfaSection;
